import { inject, Injectable } from "@angular/core";
import { from, map, Observable, of, switchMap } from "rxjs";
import { environment } from "../../../../../environments/environment";
import { API_HEADERS } from "../../../../shared/api-headers";
import { ApiClient } from "../../../../shared/data/api.client";
import { userContextStore } from "../../../../shared/data/user-context.store";
import { resolveNavigationChannel } from "../../../../shared/util/navigation-channel-config";
import { computeSiteHash } from "../../../../shared/util/site-hash";

const CMS_V1 = environment.api.services.commons.coremediaV1;

interface ApiEnvelope<T> {
  data?: T;
}

function unwrapApiResponse<T>(response: T | ApiEnvelope<T>): T {
  const wrapped = response as ApiEnvelope<T>;
  return (wrapped.data ?? response) as T;
}

// ---------------------------------------------------------------------------
// DTOs — mirror com.claas.poff.coremedia.content.dto.*
// ---------------------------------------------------------------------------

export interface ImageSourceDto {
  url: string;
  maxWidth: number;
  cropName: string;
}

export interface ImageDto {
  alt: string | null;
  title: string | null;
  width: number;
  height: number;
  src: string | null;
  srcset: ImageSourceDto[];
}

export interface CtaDto {
  enabled: boolean;
  label: string | null;
  hash: string | null;
  targetId: string | null;
  targetType: string | null;
  targetUrl: string | null;
  openInNewTab: boolean;
}

export interface VideoDto {
  dataUrl: string | null;
  fullyQualifiedUrl: string | null;
  /** Always 0 in the current payload; the video's real dimensions are not available. */
  width: number;
  height: number;
  title: string | null;
  alt: string | null;
  teaserText: string | null;
  /** Poster still, cropped to the owning item's viewType ratio — not the video's. */
  picture: ImageDto | null;
}

export interface ContentElementDto {
  id: string;
  name: string | null;
  type: string;
  viewType: string | null;
  title: string | null;
  topline: string | null;
  richTextTitle: string | null;
  teaserTitle: string | null;
  teaserText: string | null;
  detailText: string | null;
  images: ImageDto[];
  ctas: CtaDto[];
  /** Sent on every content type. Only the first entry is rendered. */
  videos: VideoDto[];
  rootName: string;
  children: ContentElementDto[];
  imageRight: boolean | null;
}

/**
 * `rootName` is the CoreMedia site root name of this specific item — it may differ from the
 * containing channel's own site (e.g. embedded content from another site). It must be used,
 * instead of the statically configured environment site name, to compute the X-Site-Hash for the
 * follow-up `/content/{id}` call (see ContentElementClient.loadElement).
 */
export interface ContentStubDto {
  id: string;
  type: string;
  name: string;
  viewType: string | null;
  rootName: string;
}

export interface PlacementDto {
  name: string;
  items: ContentStubDto[];
}

export interface ChannelLayoutDto {
  id: string;
  title: string | null;
  locale: string | null;
  placements: PlacementDto[];
}

// ---------------------------------------------------------------------------
// HTTP Clients
// ---------------------------------------------------------------------------

const EMPTY_CHANNEL_LAYOUT: ChannelLayoutDto = {
  id: "",
  title: null,
  locale: null,
  placements: [],
};

@Injectable({ providedIn: "root" })
export class ChannelLayoutClient {
  private readonly api = inject(ApiClient);
  private readonly userContext = inject(userContextStore);

  loadLayout(channelId: string): Observable<ChannelLayoutDto> {
    return this.withSiteHash((siteHash) =>
      this.api
        .get<
          ChannelLayoutDto | ApiEnvelope<ChannelLayoutDto>
        >(`${CMS_V1}/channels/${channelId}`, { headers: { [API_HEADERS.siteHash]: siteHash } })
        .pipe(map((response) => unwrapApiResponse(response))),
    );
  }

  loadLayoutWithSiteName(
    channelId: string,
    siteName: string,
  ): Observable<ChannelLayoutDto> {
    return from(computeSiteHash(siteName)).pipe(
      switchMap((siteHash) =>
        this.api
          .get<
            ChannelLayoutDto | ApiEnvelope<ChannelLayoutDto>
          >(`${CMS_V1}/channels/${channelId}`, { headers: { [API_HEADERS.siteHash]: siteHash } })
          .pipe(map((response) => unwrapApiResponse(response))),
      ),
    );
  }

  /**
   * Loads the channel layout resolved to a single locale (X-Language, falling back to
   * environment.defaultLanguage). Used for the configurable 404 channel, which lives in its own
   * CoreMedia site — hence `siteName` is passed explicitly instead of being derived from the
   * current navigation channel (see environment.notFound.siteName).
   */
  loadLocalizedLayout(
    channelId: string,
    siteName: string,
  ): Observable<ChannelLayoutDto> {
    return from(computeSiteHash(siteName)).pipe(
      switchMap((siteHash) =>
        this.api
          .get<ChannelLayoutDto | ApiEnvelope<ChannelLayoutDto>>(
            `${CMS_V1}/channels/${channelId}/localized`,
            {
              headers: {
                [API_HEADERS.fallbackLanguage]: environment.defaultLanguage,
                [API_HEADERS.siteHash]: siteHash,
              },
            },
          )
          .pipe(map((response) => unwrapApiResponse(response))),
      ),
    );
  }

  /** Resolves the current site's expected hash and passes it to the given request factory. */
  private withSiteHash(
    request: (siteHash: string) => Observable<ChannelLayoutDto>,
  ): Observable<ChannelLayoutDto> {
    const channel = resolveNavigationChannel(
      this.userContext.country(),
      this.userContext.language(),
    );
    if (!channel) {
      console.error(
        `[ChannelLayout] no channel configured for country='${this.userContext.country()}' language='${this.userContext.language()}'`,
      );
      return of(EMPTY_CHANNEL_LAYOUT);
    }
    return from(computeSiteHash(channel.siteName)).pipe(switchMap(request));
  }
}

@Injectable({ providedIn: "root" })
export class ContentElementClient {
  private readonly api = inject(ApiClient);

  /**
   * @param rootName the CoreMedia site root name of this specific content item (from
   *   `ContentStubDto.rootName`/`ContentElementDto.rootName`), NOT the statically configured
   *   environment site name — an item's site can differ from its containing channel's site.
   */
  loadElement(
    id: string,
    viewType: string,
    rootName: string,
  ): Observable<ContentElementDto> {
    return from(computeSiteHash(rootName)).pipe(
      switchMap((siteHash) =>
        this.api
          .get<ContentElementDto | ApiEnvelope<ContentElementDto>>(
            `${CMS_V1}/content/${id}`,
            {
              params: { viewType },
              headers: { [API_HEADERS.siteHash]: siteHash },
            },
          )
          .pipe(map((response) => unwrapApiResponse(response))),
      ),
    );
  }
}
